import { useState, type ReactElement, type ChangeEvent, type KeyboardEvent } from 'react';
import styled from 'styled-components';
import { IconSearch } from '@yj-intl/riff-react';
import { radius, fontSize } from '../../../../../common/css/setting/variable';

import SearchBoxSuggest from './SearchBoxSuggest';

interface IProps {
  query: string;
  suggestList: string[];
  fetchSearchSuggestion?: (word: string) => void;
}

const Wrap = styled.div`
  position: relative;
  margin-right: 4px;
`;
const InputUnitWrap = styled.div`
  display: flex;
  align-items: center;
  width: 316px;
  margin: 8px 12px;
`;

const InputUnit = styled.div`
  position: relative;
  background-color: ${props => props.theme.news.palette.bg_lv1_common};
  border: 1px solid ${props => props.theme.news.palette.btn_simple_border_common};
  border-right: none;
  border-radius: ${radius.searchLeft_pc};

  &::after {
    position: absolute;
    top: 50%;
    right: 0;
    display: block;
    height: 12px;
    content: '';
    border-right: 1px solid ${props => props.theme.news.palette.btn_simple_border_common};
    transform: translateY(-50%);
  }
`;

const InputItem = styled.input`
  width: 282px;
  height: 26px;
  padding-left: 4px;
  font-size: ${fontSize.px12};
  line-height: 26px; /* iPadの表示折り返し表示抑止 */
  border-radius: ${radius.searchLeft_pc};
  &::placeholder {
    color: rgb(0 0 0 / 43%);
  }
`;

const ButtonWrapper = styled.div`
  background-color: ${props => props.theme.news.palette.bg_lv1_common};
  border: 1px solid ${props => props.theme.news.palette.btn_simple_border_common};
  border-left: none;
  border-radius: ${radius.searchRight_pc};
`;

const SubmitButton = styled.button`
  display: flex;
  align-items: center;
  height: 26px;
  padding: 0 8px;
  cursor: pointer;
`;

const StyledIconSearch = styled(IconSearch)`
  color: ${props => props.theme.news.palette.icon_gray_lv3_common};
`;

const SearchBox = (props: IProps): ReactElement => {
  // input type="search"の文字数を判定（サジェスト表示のため）
  const [isShow, setIsShow] = useState(false);
  const [searchWord, setSearchWord] = useState(props.query);
  // サジェスト検索用の情報を格納する
  const [selectedSuggestWord, setSelectedSuggestWord] = useState(props.query);
  const [selectedSuggestIndex, setSelectedSuggestIndex] = useState(-1);
  // サジェスト一覧のワードを指定する前の検索窓の文字列を格納しておく
  const [beforeSelectSuggestWord, setBeforeSelectSuggestWord] = useState(props.query);
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const inputWord = event.currentTarget.value;
    // inputに文字が入力されていた場合
    inputWord.length !== 0 ? setIsShow(true) : setIsShow(false);
    if (props.fetchSearchSuggestion) {
      props.fetchSearchSuggestion(inputWord);
    }
    setSearchWord(inputWord);
    setSelectedSuggestWord(inputWord);
    setBeforeSelectSuggestWord(inputWord);
  };

  const handleKeyPress = (event: KeyboardEvent<HTMLInputElement>) => {
    const setStates = (suggestIndex: number, selectedWord: string) => {
      setSearchWord(selectedWord);
      setSelectedSuggestIndex(suggestIndex);
    };
    // サジェストリストがないかサジェスト一覧が表示されていない場合はkeyPress eventを処理しない
    if (!props.suggestList[0] || !isShow) {
      return;
    }
    const listLength = props.suggestList.length - 1;
    const firstSuggest = props.suggestList[0];
    const lastSuggest = props.suggestList[listLength];
    const previousSuggest = props.suggestList[selectedSuggestIndex + 1];
    const nextSuggest = props.suggestList[selectedSuggestIndex - 1];
    if (!firstSuggest || !lastSuggest || !previousSuggest || !nextSuggest) {
      return;
    }
    switch (event.keyCode) {
      // arrow up
      case 38:
        // suggestBoxで何も選択されていない状態のときは一番下のsuggest結果にフォーカスを当てる
        if (selectedSuggestIndex === -1) {
          setStates(listLength, lastSuggest);
          // suggestBoxの一番上が選択されているときは入力していたワードを表示する
        } else if (selectedSuggestIndex === 0) {
          setStates(-1, beforeSelectSuggestWord);
          // 上記以外の場合はひとつ下のsuggest結果にフォーカスを当てる
        } else {
          setStates(selectedSuggestIndex - 1, nextSuggest);
        }
        break;
      // arrow down
      case 40:
        // suggestBoxで何も選択されていない状態のときは一番上のsuggest結果にフォーカスを当てる
        if (selectedSuggestIndex === -1) {
          setStates(0, firstSuggest);
          // suggestBoxの一番下が選択されているときは入力していたワードを表示する
        } else if (selectedSuggestIndex === listLength) {
          setStates(-1, beforeSelectSuggestWord);
          // 上記以外の場合はひとつ上のsuggest結果にフォーカスを当てる
        } else {
          setStates(selectedSuggestIndex + 1, previousSuggest);
        }
        break;
    }
  };

  return (
    <form action="/search" method="get">
      <Wrap>
        <InputUnitWrap>
          <InputUnit>
            <InputItem
              type="text"
              name="p"
              placeholder="キーワードを入力"
              value={searchWord}
              onChange={handleChange}
              onBlur={() => setIsShow(false)}
              onKeyDown={handleKeyPress}
              onFocus={() => {
                searchWord.length !== 0 ? setIsShow(true) : setIsShow(false);
                if (props.fetchSearchSuggestion) {
                  props.fetchSearchSuggestion(searchWord);
                }
              }}
              autoComplete="off"
              aria-label="検索したいキーワードを入力してください"
            />
            <input type="hidden" value="utf-8" name="ei" />
            {selectedSuggestIndex !== -1 ? <input type="hidden" value={selectedSuggestIndex} name="aq" /> : null}
          </InputUnit>
          <ButtonWrapper>
            <SubmitButton type="submit" aria-label="検索ボタン" value="検索">
              <StyledIconSearch size={18} />
            </SubmitButton>
          </ButtonWrapper>
        </InputUnitWrap>
        {/* input type=searchにされたら表示 */}
        {isShow && props.suggestList.length !== 0 && (
          <SearchBoxSuggest
            isOpen={true}
            query={selectedSuggestWord}
            suggestList={props.suggestList}
            selectedSuggestIndex={selectedSuggestIndex}
            setSearchWord={setSearchWord}
            setSelectedSuggestIndex={setSelectedSuggestIndex}
          />
        )}
      </Wrap>
    </form>
  );
};
export default SearchBox;
